import clsx from "clsx";
import { uniqBy } from "lodash";
import React, { Dispatch, FC, SetStateAction, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Button } from "../buttons/button";
import { Input, Select } from "../forms";
import { Icon } from "../icon";
import { EMPTY_OPTION_VALUE } from "./constants";
import { WhatsNextFinderFiltersFormTypes } from "./types";

export interface WhatsNextFiltersPanelProps {
  labels: {
    "whats-next-finder-header-filter": string;
    "whats-next-finder-panel-apply-filters": string;
    "whats-next-finder-panel-clear-filters": string;
    "whats-next-finder-panel-order-by": string;
    "whats-next-grid-empty-state": string;
    "whats-next-finder-panel-order-by-more-recent": string;
    "whats-next-finder-panel-order-by-less-recent": string;
    "whats-next-finder-panel-search": string;
    "whats-next-finder-panel-category": string;
    "whats-next-finder-panel-all-categories": string;
  };
  whatsNextItems: Queries.WhatsNextDataFragment[];
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const WhatsNextFiltersPanel: FC<WhatsNextFiltersPanelProps> = ({
  labels,
  isOpen,
  setOpen,
  whatsNextItems,
}) => {
  const {
    register,
    formState: { isValid },
  } = useFormContext<WhatsNextFinderFiltersFormTypes>();

  const categories = useMemo(() => {
    const filteredCategories = whatsNextItems
      .flatMap((item) => item.categories)
      .filter((c) => c?.label);
    return uniqBy(filteredCategories, "label");
  }, [whatsNextItems]);

  return (
    <div
      className={clsx(
        "transition-all duration-300 rounded-3xl",
        "bg-neutral-light overflow-hidden w-full relative",
        isOpen ? "max-h-[200vh] overflow-y-auto" : "max-h-0"
      )}
    >
      <div className="overflow-y-auto p-4">
        {isOpen && (
          <button className="absolute right-3 top-3" type="button" onClick={() => setOpen(false)}>
            <Icon icon="close" className="w-6 h-6" />
          </button>
        )}
        <div
          className={clsx(
            "w-full h-full pt-3 pb-2 flex flex-col md:flex-row md:items-start gap-6 lg:gap-12"
          )}
        >
          <Input
            label={labels["whats-next-finder-panel-search"]}
            startDecoration={<Icon icon="search" className="w-5" />}
            className="w-full"
            {...register("search")}
          />
          <Select
            label={labels["whats-next-finder-panel-category"]}
            className="flex-none min-w-max"
            {...register("category")}
          >
            <option value={EMPTY_OPTION_VALUE}>
              {labels["whats-next-finder-panel-all-categories"]}
            </option>
            {categories.map(
              (category, index) =>
                category &&
                category.label && (
                  <option key={index} value={category.label}>
                    {category.label}
                  </option>
                )
            )}
          </Select>
        </div>
      </div>

      <div className="w-full flex pl-4 pb-4">
        <Button variant="primary" size="sm" type="submit" disabled={!isValid}>
          {labels["whats-next-finder-panel-apply-filters"]}
        </Button>
      </div>
    </div>
  );
};
